import { useEffect } from "react"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { DecisionOption, DecisionStatus } from "./decisionSlice"
import { selectDecisionId, selectDecisionStatus, selectLastDecision } from "./decisionSelectors"
import { AppDispatch } from "integrations/redux/store"

export type DecisionEffectContext = {
    decisionStatus: DecisionStatus
    decisionId?: string
    dispatch: AppDispatch
    lastDecision?: DecisionOption
}

/**
 * a small simple hook that standardizes how we can tap into the decision making processes
 * of the decision slice, also exposes the ability to process decisions once they are made
 *
 * @param decisionProcessor
 */
export const useDecisions = (decisionProcessor: (context: DecisionEffectContext) => void) => {
    const dispatch = useAppDispatch()

    const decisionStatus = useAppSelector(selectDecisionStatus)
    const decisionId = useAppSelector(selectDecisionId)
    const lastDecision = useAppSelector(selectLastDecision)
    const needsDecision = decisionStatus === DecisionStatus.Pending

    useEffect(() => {
        decisionProcessor({
            lastDecision,
            decisionStatus,
            decisionId,
            dispatch,
        })
    }, [lastDecision, decisionStatus, decisionId, dispatch, decisionProcessor])

    return {
        needsDecision,
    }
}

export default useDecisions
